/**
 * Pricing constants and display copy for the Premium membership.
 *
 * Display data only — call getStripePriceId() (hooks/useSubscription) for
 * the actual Stripe price ID, which depends on the runtime Stripe mode.
 *
 * The primary onboarding offer can release-gate a paid Annual + Weekly model.
 * The legacy Annual + Monthly trial picker remains bundled as a fail-closed
 * fallback until the weekly product exists and the displayed native products
 * no longer expose introductory free phases.
 */

import type { BillingTier, MembershipOfferKind } from './types';

/**
 * Premium feature list for paywalls
 */
export const PREMIUM_FEATURES = [
  {
    icon: '✓',
    text: 'New programs every week and the prompt of the day',
  },
  { icon: '✓', text: 'Mental health programs built with therapists' },
  {
    icon: '✓',
    text: 'PowerPrompt sessions with optional music and soundscapes',
  },
  {
    icon: '✓',
    text: 'Create your own PowerPrompt Programs with custom prompts and timing',
  },
];

export const WEEKLY_PRICE = 7;
export const MONTHLY_PRICE = 12;
// Annual is $97 on web, the App Store and Google Play (Sep 23 2026).
// Subscribers who joined at $49.99 between Aug 25 and Sep 23 keep the price
// attached to their Stripe or PayPal subscription; changing this display and
// checkout contract never reprices an existing subscription.
export const ANNUAL_PRICE = 97;
export const ANNUAL_REFERENCE_PRICE = 97;
// The retired native bridge can still be selected by older mobile builds when
// remote configuration fails closed. Keep its compiled display facts separate
// from the current web offer so a web price test cannot mislabel an App Store
// or Google Play purchase.
export const LEGACY_NATIVE_BRIDGE_ANNUAL_PRICE = ANNUAL_REFERENCE_PRICE;
export const DIRECT_PRICING_DEFAULT_TIER =
  'annual' as const satisfies BillingTier;
export const PAYWALL_DEFAULT_TIER = 'annual' as const satisfies BillingTier;
export const LIVE_FUNNEL_DEFAULT_TIER = 'annual' as const satisfies BillingTier;
export const WEEKLY_PRICE_CENTS = 700;
export const MONTHLY_PRICE_CENTS = 1200;
export const ANNUAL_PRICE_CENTS = 9700;
export const LEGACY_NATIVE_BRIDGE_ANNUAL_PRICE_CENTS = 9700;
// Public acquisition offer on every platform: Annual $97 with 14 days free,
// Monthly $12 with 7 days free, one introductory trial per account. Native
// surfaces still derive the exact trial from verified store phases and hide
// claims when store data is missing.
export const MONTHLY_TRIAL_PERIOD_DAYS = 7;
export const ANNUAL_TRIAL_PERIOD_DAYS = 14;
export const MONTHLY_TRIAL_PERIOD_LABEL = `${MONTHLY_TRIAL_PERIOD_DAYS} days`;
export const ANNUAL_TRIAL_PERIOD_LABEL = `${ANNUAL_TRIAL_PERIOD_DAYS} days`;
export const TRIAL_PERIOD_DAYS = ANNUAL_TRIAL_PERIOD_DAYS;
export const TRIAL_PERIOD_LABEL = ANNUAL_TRIAL_PERIOD_LABEL;

/**
 * The one always-on (static / SSR) statement of web Premium pricing, used by
 * SEO articles, comparison tables, and landing notes that cannot read the
 * runtime native layout. The standard web offer always includes paid Annual
 * and Monthly. Client-rendered surfaces should prefer useBridgeAnnualVisible.
 */
export const PREMIUM_PRICING_SENTENCE = `Premium is $${ANNUAL_PRICE}/year after ${ANNUAL_TRIAL_PERIOD_DAYS} days free, or $${MONTHLY_PRICE}/month after ${MONTHLY_TRIAL_PERIOD_DAYS} days free. Cancel anytime.`;
export const PREMIUM_PRICING_TABLE_CELL = `$${ANNUAL_PRICE}/year (${ANNUAL_TRIAL_PERIOD_DAYS} days free) or $${MONTHLY_PRICE}/month (${MONTHLY_TRIAL_PERIOD_DAYS} days free)`;
export const PREMIUM_MONTHLY_PRICING_SENTENCE = `Premium is $${MONTHLY_PRICE}/month after ${MONTHLY_TRIAL_PERIOD_DAYS} days free. Cancel anytime.`;

/**
 * Pricing tiers (display data only)
 */
export const PRICING_TIERS = {
  weekly: {
    id: 'weekly',
    name: 'Weekly',
    price: WEEKLY_PRICE,
    interval: 'week',
  },
  monthly: {
    id: 'monthly',
    name: 'Monthly',
    price: MONTHLY_PRICE,
    interval: 'month',
  },
  annual: {
    id: 'annual',
    name: 'Annual',
    price: ANNUAL_PRICE,
    interval: 'year',
  },
} as const;

export const LEGACY_NATIVE_BRIDGE_PRICING_TIERS = {
  weekly: PRICING_TIERS.weekly,
  monthly: PRICING_TIERS.monthly,
  annual: {
    id: 'annual',
    name: 'Annual',
    price: LEGACY_NATIVE_BRIDGE_ANNUAL_PRICE,
    interval: 'year',
  },
} as const;

export const LEGACY_NATIVE_BRIDGE_ANNUAL_SAVINGS_DOLLARS =
  LEGACY_NATIVE_BRIDGE_PRICING_TIERS.monthly.price * 12 -
  LEGACY_NATIVE_BRIDGE_PRICING_TIERS.annual.price;
export const LEGACY_NATIVE_BRIDGE_ANNUAL_SAVINGS_PERCENT = Math.round(
  (LEGACY_NATIVE_BRIDGE_ANNUAL_SAVINGS_DOLLARS /
    (LEGACY_NATIVE_BRIDGE_PRICING_TIERS.monthly.price * 12)) *
    100,
);
export const LEGACY_NATIVE_BRIDGE_ANNUAL_WEEKLY_EQUIVALENT_LABEL = `$${(
  LEGACY_NATIVE_BRIDGE_PRICING_TIERS.annual.price / 52
).toFixed(2)}/week`;

export const ANNUAL_SAVINGS_CENTS =
  MONTHLY_PRICE_CENTS * 12 - ANNUAL_PRICE_CENTS;
export const ANNUAL_SAVINGS_DOLLARS = ANNUAL_SAVINGS_CENTS / 100;
export const ANNUAL_SAVINGS_AMOUNT_LABEL = `$${ANNUAL_SAVINGS_DOLLARS.toFixed(2)}`;
export const ANNUAL_SAVINGS_LABEL = `${ANNUAL_SAVINGS_AMOUNT_LABEL}/year`;
export const ANNUAL_SAVINGS_CONTEXT = `vs paying $${PRICING_TIERS.monthly.price}/month`;
export const ANNUAL_SAVINGS_PERCENT = Math.round(
  (ANNUAL_SAVINGS_DOLLARS / (PRICING_TIERS.monthly.price * 12)) * 100,
);
export const ANNUAL_MONTHLY_EQUIVALENT = PRICING_TIERS.annual.price / 12;
export const ANNUAL_MONTHLY_EQUIVALENT_LABEL = `$${ANNUAL_MONTHLY_EQUIVALENT.toFixed(2)}/month`;

// Per-week framing: the annual plan costs less than $2 a week, which is the
// anchor line next to the $7 weekly plan and the daily content cadence.
export const ANNUAL_WEEKLY_EQUIVALENT = PRICING_TIERS.annual.price / 52;
export const ANNUAL_WEEKLY_EQUIVALENT_LABEL = `$${ANNUAL_WEEKLY_EQUIVALENT.toFixed(2)}/week`;
export const WEEKLY_ANNUALIZED_DOLLARS = Math.round(WEEKLY_PRICE * 52);
export const ANNUAL_VS_WEEKLY_SAVINGS_PERCENT = Math.round(
  ((WEEKLY_ANNUALIZED_DOLLARS - PRICING_TIERS.annual.price) /
    WEEKLY_ANNUALIZED_DOLLARS) *
    100,
);

/**
 * $67 first-year Annual offer, renewing at $97. Web charges the $97 annual
 * price with a $30-off first-year coupon; native uses a dedicated store
 * product with a $67 introductory price.
 *
 * This offer is intentionally excluded from the primary onboarding paywall.
 * Keep it available only for explicit cancellation saves, win-back journeys,
 * or limited promotional surfaces.
 */
export const ONBOARDING_YEARLY_OFFER_PRICE = 67;
export const ONBOARDING_YEARLY_OFFER_PRICE_CENTS = 6700;
export const ONBOARDING_YEARLY_OFFER_RENEWAL_PRICE =
  LEGACY_NATIVE_BRIDGE_ANNUAL_PRICE;
export const ONBOARDING_YEARLY_OFFER_COMPARE_AT_CENTS =
  MONTHLY_PRICE_CENTS * 12;
export const ONBOARDING_YEARLY_OFFER_SAVINGS_PERCENT = Math.round(
  ((ONBOARDING_YEARLY_OFFER_COMPARE_AT_CENTS -
    ONBOARDING_YEARLY_OFFER_PRICE_CENTS) /
    ONBOARDING_YEARLY_OFFER_COMPARE_AT_CENTS) *
    100,
);
// The weekly-anchor comparison for the $67 first-year offer (USD framing;
// native storefronts localize the price strings but keep this USD percent).
export const ONBOARDING_YEARLY_OFFER_VS_WEEKLY_SAVINGS_PERCENT = Math.round(
  ((WEEKLY_ANNUALIZED_DOLLARS - ONBOARDING_YEARLY_OFFER_PRICE) /
    WEEKLY_ANNUALIZED_DOLLARS) *
    100,
);

export function isOnboardingYearlyOfferKind(
  tier: BillingTier,
  offerKind: MembershipOfferKind,
): boolean {
  return tier === 'annual' && offerKind === 'onboarding_yearly';
}

export function isAnnualIntroOfferKind(
  tier: BillingTier,
  offerKind: MembershipOfferKind,
): boolean {
  return tier === 'annual' && offerKind === 'annual_trial';
}

export function isMonthlyTrialOfferKind(
  tier: BillingTier,
  offerKind: MembershipOfferKind,
): boolean {
  return tier === 'monthly' && offerKind === 'monthly_trial';
}

/**
 * VIP and Pro list prices in USD (plan section 1; the server's
 * VIP_PRO_EXPECTED_AMOUNT_CENTS in supabase/functions/_shared/vipProCheckout.ts).
 * Analytics fallback values, plus the static web marketing copy below
 * (signed-out pages cannot run a quote). Checkout prices and the plan picker
 * still come from the live store or server quote, never from these.
 */
export const VIP_PRO_LIST_PRICES = {
  vip: { monthly: 9.99, annual: 49.99 },
  pro: { monthly: 19.99, annual: 149 },
} as const;

function usdLabel(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

export const VIP_ANNUAL_PRICE_LABEL = usdLabel(VIP_PRO_LIST_PRICES.vip.annual);
export const VIP_MONTHLY_PRICE_LABEL = usdLabel(VIP_PRO_LIST_PRICES.vip.monthly);
export const PRO_ANNUAL_PRICE_LABEL = usdLabel(VIP_PRO_LIST_PRICES.pro.annual);

export type VipProPlanTier = keyof typeof VIP_PRO_LIST_PRICES;
export type VipProTrackedOfferKind =
  | 'vip_upfront'
  | 'vip_rescue_annual_trial'
  | 'pro_upgrade';

export function isVipProOfferKind(
  offerKind: unknown,
): offerKind is VipProTrackedOfferKind {
  return (
    offerKind === 'vip_upfront' ||
    offerKind === 'vip_rescue_annual_trial' ||
    offerKind === 'pro_upgrade'
  );
}

/** 'pro' for the Pro upgrade, 'vip' for the other VIP kinds, null for legacy kinds. */
export function vipProPlanTier(offerKind: unknown): VipProPlanTier | null {
  if (!isVipProOfferKind(offerKind)) return null;
  return offerKind === 'pro_upgrade' ? 'pro' : 'vip';
}

/**
 * Fallback conversion value (USD list price) for a VIP or Pro offer when no
 * store or server quote reached the tracking call. Null for legacy offer
 * kinds and for tiers VIP and Pro do not sell (weekly), so callers keep their
 * existing Premium fallback. Trial events still report 0; callers decide that
 * before asking for a fallback.
 */
export function vipProFallbackValue(
  offerKind: unknown,
  tier: string | null | undefined,
): number | null {
  const planTier = vipProPlanTier(offerKind);
  if (!planTier) return null;
  if (tier !== 'monthly' && tier !== 'annual') return null;
  return VIP_PRO_LIST_PRICES[planTier][tier];
}

/** "$49.99" from a quote; whole dollars keep their cents so prices read the same everywhere. */
export function formatQuotedPrice(price: { amount_cents: number; currency: string }): string {
  // Whole amounts read "$149", as in the site copy. Zero stays "$0.00".
  const digits = price.amount_cents > 0 && price.amount_cents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency.toUpperCase(),
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(price.amount_cents / 100);
}
